import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, Grid, Divider, Stack } from "@mui/material";
import MainTitle from "../../../Components/Common/MainTitle/MainTitle";
import TextFieldRenderer from "../../../Components/Form/TextFieldRenderer";

import {
  GET_BAN_REPORTS,
  REVIEW_BAN_REPORT,
  UNBAN_USER,
} from "../../../graphql/banReportGraphql";
import useGraphql from "../../../Hooks/useGraphql";
import useLog from "../../../Hooks/useLog";
import { useTranslation } from "../../../context/Internationalization";
import { SnackBarContext } from "../../../context/SnackBarContext";
import useInfinityScroll from "../../../Hooks/useInfinityScroll";
import { useDebouncedSearch } from "../../../Hooks/useDebouncedSearch";

import boxStyles from "../../../styles/boxing";
import BanReportsTable from "./BanReportsTable/BanReportsTable";
import BanReportReviewModal from "./Components/BanReportReviewModal/BanReportReviewModal";
import { banReportStatusOptions } from "../../../utility/Enum";
import FilterSelect from "../../../Components/Common/Filters/FilterSelect";

const SuperAdminBanReports = () => {
  const { t } = useTranslation();
  const snackBar = useContext(SnackBarContext);
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [extraRows, setExtraRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const [state, setState] = useState({
    reviewModal: false,
    selectedReport: null,
    loading: false,
    unbanning: false,
  });

  const fetchReports = useCallback(
    async (term, status) => {
      const res = await axiosGraphQL({
        query: GET_BAN_REPORTS,
        variables: {
          term,
          status: status ? status : undefined,
          skip: 0,
          limit: 20,
          sortingField: "createdAt",
          sortingDir: "desc",
        },
      });
      const results = res?.data?.data?.getBanReports || [];
      setExtraRows([]);
      setHasMore(results.length === 20);
      return results;
    },
    [axiosGraphQL]
  );

  const {
    options: reports,
    loading,
    debouncedTerm,
    refetch,
  } = useDebouncedSearch({
    searchTerm,
    fetchOptions: (term) => fetchReports(term, statusFilter),
  });

  const loadMore = useCallback(async () => {
    try {
      const currentSkip = reports.length + extraRows.length;
      const response = await axiosGraphQL({
        query: GET_BAN_REPORTS,
        variables: {
          term: debouncedTerm,
          status: statusFilter ? statusFilter : undefined,
          skip: currentSkip,
          limit: 20,
          sortingField: "createdAt",
          sortingDir: "desc",
        },
      });

      const more = response?.data?.data?.getBanReports || [];
      const existingIds = new Set([
        ...reports.map((r) => r._id),
        ...extraRows.map((r) => r._id),
      ]);
      const deduped = more.filter((r) => !existingIds.has(r._id));
      setExtraRows((prev) => [...prev, ...deduped]);
      setHasMore(more.length === 20);
    } catch (err) {
      errorLog(err, "SuperAdminBanReports", "loadMore", "GET_BAN_REPORTS");
    }
  }, [debouncedTerm, reports, extraRows, statusFilter]);

  const lastElementRef = useInfinityScroll({
    updatePage: loadMore,
    isLoading: loading,
    hasMore,
  });

  const allReports = useMemo(
    () => [
      ...new Map([...reports, ...extraRows].map((r) => [r._id, r])).values(),
    ],
    [reports, extraRows]
  );

  const onChangeFilter = ({ target: { name, value } }) => {
    if (name === "term") setSearchTerm(value);
  };

  const onChangeStatus = (e) => {
    setStatusFilter(e.target.value);
  };

  // when status changes, refetch through the hook (which already closes over the latest statusFilter)
  useEffect(() => {
    setExtraRows([]);
    setHasMore(false);
    refetch(); // useDebouncedSearch will call fetchOptions(term) with the new statusFilter
  }, [statusFilter]);

  const openReview = (report) =>
    setState((s) => ({ ...s, reviewModal: true, selectedReport: report }));
  const closeReview = () =>
    setState((s) => ({ ...s, reviewModal: false, selectedReport: null }));

  const submitReview = async ({ action, decisionNotes }) => {
    try {
      setState((s) => ({ ...s, loading: true }));
      await axiosGraphQL({
        query: REVIEW_BAN_REPORT,
        variables: {
          banReport: {
            reportId: state.selectedReport?._id,
            action, // "accept" | "reject"
            decisionNotes,
          },
        },
      });

      snackBar.setOptions({
        alertMessage: t("ban_report_reviewed_successfully"),
        alertSeverity: "success",
      });
      snackBar.setSnackbarOpen(true);

      closeReview();
      refetch();
    } catch (err) {
      errorLog(
        err,
        "SuperAdminBanReports",
        "submitReview",
        "REVIEW_BAN_REPORT"
      );
      snackBar.setOptions({
        alertMessage: err.message,
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const unbanUser = async (userId) => {
    if (!userId) return;
    try {
      setState((s) => ({ ...s, unbanning: true }));
      await axiosGraphQL({
        query: UNBAN_USER,
        variables: { userId },
      });

      // Optimistic update in the open modal
      setState((s) => ({
        ...s,
        selectedReport: s.selectedReport
          ? {
              ...s.selectedReport,
              reportedUser: s.selectedReport.reportedUser
                ? { ...s.selectedReport.reportedUser, isBanned: false }
                : s.selectedReport.reportedUser,
            }
          : s.selectedReport,
      }));

      snackBar.setOptions({
        alertMessage: t("user_unbanned_successfully"),
        alertSeverity: "success",
      });
      snackBar.setSnackbarOpen(true);

      // Refresh table state
      refetch();
    } catch (err) {
      errorLog(err, "SuperAdminBanReports", "unbanUser", "UNBAN_USER");
      snackBar.setOptions({
        alertMessage: err.message,
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    } finally {
      setState((s) => ({ ...s, unbanning: false }));
    }
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={boxStyles.headerContainer} component="div">
            <Stack direction="row" spacing={2}>
              <MainTitle title={t("ban_reports_admin_panel")} />
            </Stack>
            <Stack direction="row" spacing={2}>
              {/* Reserved for future actions (bulk, export, etc.) */}
            </Stack>
          </Box>
          <Divider />
        </Grid>

        <Grid
          item
          xs={12}
          style={{ ...boxStyles.filtersContainer, paddingTop: 16 }}
        >
          <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
            <Grid item xs={8}>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Grid container item xs={12} sm={6}>
                  <TextFieldRenderer
                    label={t("search_in_comments_decision_notes")}
                    onChange={onChangeFilter}
                    name="term"
                    value={searchTerm}
                  />
                </Grid>
                <Box sx={{ minWidth: 110, paddingTop: 3 }}>
                  <FilterSelect
                    onChange={onChangeStatus}
                    value={statusFilter}
                    name={"status"}
                    options={banReportStatusOptions(t)}
                  />
                </Box>
              </Stack>
            </Grid>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          {allReports.length === 0 && !loading ? (
            <span>{t("no_reports_found")}</span>
          ) : (
            <BanReportsTable
              key={statusFilter}
              lastElementRef={lastElementRef}
              reports={allReports}
              loading={loading}
              onOpenReview={openReview}
            />
          )}
        </Grid>
      </Grid>

      <BanReportReviewModal
        open={state.reviewModal}
        loading={state.loading}
        onClose={closeReview}
        onSubmit={submitReview}
        onUnban={unbanUser}
        unbanning={state.unbanning}
        report={state.selectedReport}
      />
    </Box>
  );
};

export default SuperAdminBanReports;
